'use client'

import { RefreshCcwIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { TwapUnitInput } from '@/features/order/order-form/components/widget/twap-unit-input'
import { TWAP_INTERVAL_PRESETS } from '@/lib/configs/twap-config'
import { cn } from '@/lib/utils'

const INTERVAL_PRESETS = TWAP_INTERVAL_PRESETS

interface TwapIntervalInputProps {
  value: string
  onChange: (value: string) => void
}

// Convert total seconds to days, hours, mins, secs
function secondsToDHMS(totalSeconds: number): {
  days: string
  hours: string
  mins: string
  secs: string
} {
  if (isNaN(totalSeconds) || totalSeconds <= 0) {
    return { days: '', hours: '', mins: '', secs: '' }
  }
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60
  return {
    days: days > 0 ? days.toString() : '',
    hours: hours > 0 ? hours.toString() : '',
    mins: mins > 0 ? mins.toString() : '',
    secs: secs > 0 ? secs.toString() : '',
  }
}

// Convert days, hours, mins, secs to total seconds
function dhmsToSeconds(days: string, hours: string, mins: string, secs: string): number {
  const d = parseInt(days) || 0
  const h = parseInt(hours) || 0
  const m = parseInt(mins) || 0
  const s = parseInt(secs) || 0
  return d * 86400 + h * 3600 + m * 60 + s
}

/**
 * TWAP interval input with Days/Hours/Mins/Secs fields and quick preset buttons.
 * value / onChange are in **seconds** (previously minutes).
 */
export function TwapIntervalInput({ value, onChange }: TwapIntervalInputProps) {
  const t = useTranslations('orderForm')
  const totalSeconds = parseInt(value) || 0

  // Local state for individual fields
  const [days, setDays] = useState('')
  const [hours, setHours] = useState('')
  const [mins, setMins] = useState('')
  const [secs, setSecs] = useState('')

  // Handle individual field changes
  const handleFieldChange = (field: 'days' | 'hours' | 'mins' | 'secs', val: string) => {
    if (val !== '' && !/^\d*$/.test(val)) return

    let newDays = days
    let newHours = hours
    let newMins = mins
    let newSecs = secs

    if (field === 'days') {
      newDays = val
      setDays(val)
    } else if (field === 'hours') {
      newHours = val
      setHours(val)
    } else if (field === 'mins') {
      newMins = val
      setMins(val)
    } else {
      newSecs = val
      setSecs(val)
    }

    const total = dhmsToSeconds(newDays, newHours, newMins, newSecs)
    onChange(total > 0 ? total.toString() : '')
  }

  // Handle preset button click — adds to the current value
  const handlePresetClick = (seconds: number) => {
    onChange((totalSeconds + seconds).toString())
  }
  const [isSpinning, setIsSpinning] = useState(false)

  // Handle reset button click — clears the value and triggers animation
  const handleReset = () => {
    setIsSpinning(true)
    onChange('')
    setTimeout(() => {
      setIsSpinning(false)
    }, 500)
  }

  // Sync local state when value changes from outside (e.g., preset buttons)
  useEffect(() => {
    const { days: d, hours: h, mins: m, secs: s } = secondsToDHMS(totalSeconds)
    setDays(d)
    setHours(h)
    setMins(m)
    setSecs(s)
  }, [totalSeconds])

  return (
    <div className="relative flex w-full items-center">
      <div className="flex w-full min-w-0 flex-col gap-2">
        {/* Days / Hours / Mins / Secs inputs */}
        <div data-tutorial="duration" className="grid grid-cols-4 gap-2">
          <div className="flex min-w-0 flex-col gap-1">
            <TwapUnitInput
              inputMode="numeric"
              value={days}
              onChange={(e) => handleFieldChange('days', e.target.value)}
              placeholder=""
              unit={t('days')}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <TwapUnitInput
              inputMode="numeric"
              value={hours}
              onChange={(e) => handleFieldChange('hours', e.target.value)}
              placeholder=""
              unit={t('hoursShort')}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <TwapUnitInput
              inputMode="numeric"
              value={mins}
              onChange={(e) => handleFieldChange('mins', e.target.value)}
              placeholder=""
              unit={t('mins')}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <TwapUnitInput
              inputMode="numeric"
              value={secs}
              onChange={(e) => handleFieldChange('secs', e.target.value)}
              placeholder=""
              unit={t('secs')}
            />
          </div>
        </div>

        {/* Preset buttons */}
        <div className="grid grid-cols-8 gap-2">
          {INTERVAL_PRESETS.map((preset) => {
            const isActive = totalSeconds === preset.seconds
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => handlePresetClick(preset.seconds)}
                className={cn([
                  'flex h-6.5 items-center justify-center text-xs font-medium transition-colors not-disabled:cursor-pointer',
                  'border-foreground/10 bg-foreground/5 text-foreground/60 hover:bg-foreground/10 hover:text-foreground border',
                  isActive && 'bg-emphasis text-emphasis-foreground',
                ])}
              >
                {preset.label}
              </button>
            )
          })}
        </div>
      </div>
      {/* <Button
        size="icon"
        variant="ghost"
        className="absolute bottom-2 left-full translate-x-1 translate-y-0"
        onClick={handleReset}
      >
        <RefreshCcwIcon
          className={cn(
            'text-foreground/40 hover:text-foreground/60 size-2.5',
            isSpinning && 'animate-spin'
          )}
        />
      </Button> */}
    </div>
  )
}
